/**
 * Übersetzung Routenname -> sprechende Bezeichnung, EINMAL zentral für die
 * gesamte Nutzungsauswertung. Hintergrund: activity_events.route trägt den
 * technischen Laravel-Routennamen (z. B. "order", "stock.overview.data"),
 * niemals einen für Menschen lesbaren Text (siehe TrackActivity::erfassen -
 * dort wird ausschließlich $request->route()?->getName() geschrieben). Wer
 * "wo befindet sich jemand gerade" beantworten will, darf diesen Rohwert
 * nirgends direkt anzeigen, sonst liest der Nutzer "stock.overview.data"
 * statt "Bestandsübersicht – Daten".
 *
 * Pflege: Jede neue benannte Route, die in einer Nutzungsauswertung als
 * "Seite" auftauchen kann, bekommt hier einen Eintrag. Fehlt einer, greift
 * der Rückfall am Ende der Datei - kein Absturz, aber ein weniger
 * sprechender Text.
 */
const ROUTE_LABELS = {
    dashboard: "Dashboard",

    // Artikel/Produkte
    product: "Artikelübersicht",
    "product.create": "Artikel anlegen",
    "product.edit": "Artikel bearbeiten",
    "product.show": "Artikeldetails",
    "product.convert": "Artikel konvertieren",
    "product.rebalance": "Artikel-Rebalancing",
    "product.save": "Artikel speichern",
    "product.delete": "Artikel löschen",

    // Bestellungen
    order: "Bestellübersicht",
    "order.show": "Bestelldetails",
    "order.preview": "Bestellvorschau",
    "order.edit": "Bestellung bearbeiten",
    "order.download": "Bestellung – PDF",
    "order.csv": "Bestellung – CSV-Export",
    "order.recreate": "Bestellung neu erzeugen",
    "order.resend": "Bestellung erneut senden",
    "order.transfer": "Bestellung übertragen",
    "order.assign": "Bestellung zuweisen",
    "order.unassign": "Bestellung – Zuweisung aufheben",
    "order.seen": "Bestellung als gesehen markiert",

    // Import/Export
    import: "Import",
    "import.upload": "Import – Datei hochladen",
    "import.run": "Import ausführen",
    "export.index": "Export",
    "export.download": "Export-Download",

    // Kunden
    "customer.index": "Kundenübersicht",
    "customer.show": "Kundendetails",

    // Bestand/Lager
    "stock.overview": "Bestandsübersicht",
    "stock.overview.data": "Bestandsübersicht – Daten",
    "stock.overview.groups": "Bestandsübersicht – Gruppen",
    "stock.overview.export": "Bestandsübersicht – Export",
    "stock.overview.refresh-article": "Bestand – Artikel aktualisieren",
    "stock.overview.refresh-stock-data": "Bestand – Daten aktualisieren",
    "stock.overview.reservations": "Bestand – Reservierungen",
    "stock.overview.data-freshness": "Bestand – Datenaktualität",
    "stock.overview.data-freshness.all": "Bestand – alle Quellen aktualisieren",
    "stock.overview.data-freshness.refresh": "Bestand – Quelle aktualisieren",
    "stock.overview.views.index": "Bestand – gespeicherte Ansichten",
    "stock.overview.views.store": "Bestand – Ansicht speichern",
    "stock.overview.views.update": "Bestand – Ansicht ändern",
    "stock.overview.views.destroy": "Bestand – Ansicht löschen",
    "stock.overview.views.reorder": "Bestand – Ansichten sortieren",
    "stock.overview.saved-searches.store": "Bestand – Suche speichern",
    "stock.overview.saved-searches.destroy": "Bestand – Suche löschen",
    "stock.b2b-availability": "B2B-Lagerverfügbarkeit",
    "stock.b2b-availability.run-detail": "B2B-Lagerverfügbarkeit – Lauf-Details",
    "stock.b2c-availability": "B2C-Bestandsaktualisierung",
    "stock.b2c-availability.run-detail": "B2C-Bestandsaktualisierung – Lauf-Details",
    "stock.b2c-availability.status": "B2C-Bestandsaktualisierung – Status",
    "stock.b2c-availability.trigger": "B2C-Bestandsaktualisierung – Anstoßen",
    "stock.change-log": "Bestands-Änderungslog",
    "stock.change-log.data": "Bestands-Änderungslog – Daten",
    "stock.change-log.article-history": "Bestands-Änderungslog – Artikelverlauf",

    // Profil/Anmeldung
    profil: "Mein Profil",
    "profil.save": "Profil speichern",
    break: "Pause",
    login: "Anmeldung",
    "login.post": "Anmeldung",
    logout: "Abmeldung",
    "password.change.index": "Passwort ändern",
    "password.change": "Passwort ändern",

    // Rollen/Rechte
    "roles.index": "Rollenverwaltung",
    "roles.store": "Rolle anlegen",
    "roles.update": "Rolle bearbeiten",
    "roles.destroy": "Rolle löschen",
    "roles.permissions.sync": "Rollenrechte zuordnen",
    "permissions.store": "Recht anlegen",

    // Benutzer/Abteilungen
    user: "Benutzerübersicht",
    "users.index": "Benutzerübersicht",
    "user.create": "Benutzer anlegen",
    "user.edit": "Benutzer bearbeiten",
    "user.save": "Benutzer speichern",
    "user.copysettings": "Benutzereinstellungen kopieren",
    "users.quick-access": "Benutzer – Schnellzugriff",
    "users.roles-inline": "Benutzer – Rollen zuordnen",
    "users.departments-inline": "Benutzer – Abteilung zuordnen",
    "users.active": "Benutzer – Aktivstatus geändert",
    "users.protected-permissions": "Benutzer – geschützte Rechte",
    "users.roles.assign": "Benutzer – Rolle zugewiesen",
    "departments.index": "Abteilungen",
    "departments.store": "Abteilung anlegen",
    "departments.update": "Abteilung bearbeiten",

    // OAuth/Einstellungen
    "oauth.index": "OAuth-Verbindungen",
    "oauth.start": "OAuth – Verbindung starten",
    "oauth.return": "OAuth – Rückkehr",
    "settings.index": "Einstellungen",
    "settings.update": "Einstellungen speichern",

    // E-Mail-Kampagnen
    "campaigns.index": "Kampagnenübersicht",
    "campaigns.create": "Kampagne anlegen",
    "campaigns.edit": "Kampagne bearbeiten",
    "campaigns.store": "Kampagne speichern",
    "campaigns.update": "Kampagne bearbeiten",
    "campaigns.destroy": "Kampagne gelöscht",
    "campaigns.test-mail": "Kampagne – Test-Mail",
    "campaigns.signatures.index": "Kampagnen-Signaturen",
    "campaigns.signatures.create": "Signatur anlegen",
    "campaigns.signatures.edit": "Signatur bearbeiten",
    "campaigns.signatures.store": "Signatur speichern",
    "campaigns.signatures.update": "Signatur bearbeiten",
    "campaigns.signatures.destroy": "Signatur gelöscht",
    "campaigns.mailboxes.index": "Kampagnen-Absender",
    "campaigns.mailboxes.add": "Absender hinzugefügt",
    "campaigns.mailboxes.discover": "Absender gesucht",
    "campaigns.mailboxes.toggle": "Absender umgeschaltet",
    "campaigns.images.store": "Kampagnenbild hochgeladen",
    "campaigns.images.show": "Kampagnenbild",
    "campaigns.table-preferences.store": "Kampagnentabelle – Ansicht gespeichert",
    "campaigns.recipients.index": "Kampagnenempfänger",
    "campaigns.recipients.import": "Kampagnenempfänger importiert",
    "campaigns.recipients.import.analyze": "Kampagnenempfänger – Import geprüft",
    "campaigns.recipients.feedback": "Empfänger-Rückmeldung",
    "campaigns.recipients.weclapp": "Empfänger zu Weclapp übertragen",
    "campaigns.send.start": "Kampagnenversand gestartet",
    "campaigns.send.pause": "Kampagnenversand pausiert",
    "campaigns.send.resume": "Kampagnenversand fortgesetzt",
    "campaigns.send.stop": "Kampagnenversand gestoppt",
    "campaigns.send.progress": "Kampagnenversand – Fortschritt",

    // Roadmap
    "roadmap.index": "Roadmap",
    "roadmap.store": "Roadmap-Eintrag angelegt",
    "roadmap.update": "Roadmap-Eintrag bearbeitet",
    "roadmap.destroy": "Roadmap-Eintrag gelöscht",
    "roadmap.move": "Roadmap-Eintrag verschoben",

    // Verkaufsstatistik
    "statistics.index": "Verkaufsstatistik – Überblick",
    "statistics.sortimente": "Verkaufsstatistik – Sortimente",
    "statistics.artikel": "Verkaufsstatistik – Artikel",
    "statistics.renner-penner": "Verkaufsstatistik – Renner & Penner",
    "statistics.lagerstrategie": "Verkaufsstatistik – Lagerstrategie",
    "statistics.lagerplaetze": "Verkaufsstatistik – Lagerplätze",
    "statistics.sortiment-verlauf": "Verkaufsstatistik – Sortiment-Verlauf",
    "statistics.sortiment-detail": "Verkaufsstatistik – Sortiment-Detail",
    "statistics.export": "Verkaufsstatistik – Export",
    "statistics.inventory-strategy.herleitung": "Lagerstrategie – Herleitung",
    "statistics.inventory-strategy.flaechenbedarf": "Lagerstrategie – Flächenbedarf",
    "statistics.inventory-strategy.export": "Lagerstrategie – Export",

    // Weclapp
    "weclapp.quotations": "Weclapp – Angebote",
    "weclapp.sales-orders": "Weclapp – Aufträge",
    "weclapp.shipments": "Weclapp – Lieferungen",
    "weclapp.credit-memos": "Weclapp – Gutschriften",
    "weclapp.purchase-orders": "Weclapp – Einkaufsbestellungen",
    "weclapp.incoming-goods": "Weclapp – Wareneingänge",
    "weclapp.top-mapping.index": "Weclapp – Zahlungsarten",
    "weclapp.transmission-jobs": "Weclapp – Übertragungen",
    "weclapp.transmission.status": "Weclapp – Übertragungsstatus",
    "weclapp.transmission.retry": "Weclapp – Übertragung wiederholt",
    "weclapp.customer.find": "Weclapp – Kunde gesucht",
    "weclapp.customer.create": "Weclapp – Kunde angelegt",
    "weclapp.order.create": "Weclapp – Bestellung angelegt",
    "weclapp.order.precheck": "Weclapp – Lieferadresse geprüft",
    "weclapp.order.unlink": "Weclapp – Bestellung getrennt",
    "weclapp.import.analyze": "Weclapp-Import – Prüfung",
    "weclapp.import.commit": "Weclapp-Import ausgeführt",

    // Support
    "support.tickets.index": "Meine Support-Tickets",
    "support.tickets.assigned": "Zugewiesene Tickets",
    "support.tickets.assigned.show": "Zugewiesenes Ticket",
    "support.tickets.assigned.attachment": "Ticket-Anhang",
    "support.tickets.show": "Support-Ticket",
    "support.tickets.attachment": "Ticket-Anhang",
    "support.tickets.store": "Ticket erstellt",
    "support.tickets.comment": "Ticket kommentiert",
    "support.tickets.close": "Ticket geschlossen",

    // Nutzungsauswertung selbst
    "nutzung.index": "Nutzung – Überblick",
    "nutzung.mitarbeiter": "Nutzung – Mitarbeiter",
    "nutzung.profil": "Nutzung – Mitarbeiterprofil",
    "nutzung.funktionen": "Nutzung – Funktionen",
    "nutzung.live": "Nutzung – Live",
    "nutzung.ereignisse": "Nutzung – Ereignisse",
    "nutzung.einstellungen": "Nutzung – Einstellungen",

    activitylogs: "Aktivitätsprotokoll",
};

/**
 * Grobe Übersetzung des ERSTEN Routensegments für den Rückfall - dieselbe
 * Zuordnung wie config/activity.php (Spezifikation Abschnitt 5), aber als
 * Wort statt als Bereichs-Schlüssel, weil der Rückfall lesbarer sein soll
 * als "sonstiges – Xyz".
 */
const SEGMENT_WORTE = {
    order: "Bestellung",
    product: "Artikel",
    item: "Artikel",
    stock: "Bestand",
    statistics: "Verkaufsstatistik",
    user: "Benutzer",
    users: "Benutzer",
    roles: "Rolle",
    permissions: "Recht",
    departments: "Abteilung",
    campaign: "Kampagne",
    campaigns: "Kampagne",
    mail: "Mail",
    support: "Support",
    ticket: "Ticket",
    roadmap: "Roadmap",
    import: "Import",
    export: "Export",
    settings: "Einstellungen",
    nutzung: "Nutzung",
    weclapp: "Weclapp",
    customer: "Kunde",
    oauth: "OAuth",
    profil: "Profil",
};

/**
 * Ein einzelnes Routensegment ("data-freshness", "sales-orders") in einen
 * lesbaren Bruchstück-Text - nur für den Rückfall gedacht, deshalb bewusst
 * simpel (Bindestrich zu Leerzeichen, erster Buchstabe groß).
 */
function segmentLesbar(segment) {
    if (!segment) {
        return "";
    }
    const text = segment.replace(/[-_]/g, " ").trim();
    return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * Rückfall für Routen ohne Eintrag in ROUTE_LABELS: erstes Segment über
 * SEGMENT_WORTE übersetzen, den Rest lesbar zusammensetzen. Liefert nie
 * die rohe Route unverändert zurück - lieber ein grober, aber sprechender
 * Text als ein technischer Bezeichner in der Oberfläche.
 */
function rueckfall(route) {
    const segmente = route.split(/[.\/]/).filter(Boolean);
    if (segmente.length === 0) {
        return "Unbekannter Bereich";
    }
    const [erstes, ...rest] = segmente;
    const kopf = SEGMENT_WORTE[erstes.toLowerCase()] || segmentLesbar(erstes);
    const restText = rest.map(segmentLesbar).filter(Boolean).join(" – ");
    return restText ? `${kopf} – ${restText}` : kopf;
}

/**
 * Sprechende Bezeichnung für einen Routennamen (oder ersatzweise einen
 * Pfad, falls TrackActivity keine benannte Route fand) - die EINZIGE
 * Stelle im Frontend, die activity_events.route in Klartext übersetzt.
 * Nirgends sonst darf ein Routenname direkt angezeigt werden.
 */
export function seitenName(route) {
    if (!route || route === "-") {
        return "–";
    }
    if (ROUTE_LABELS[route]) {
        return ROUTE_LABELS[route];
    }
    return rueckfall(route);
}

export default ROUTE_LABELS;
